import { Divider } from "@mui/material";
import { useTranslations } from "use-intl";
import type { GetFlightAvailabilityTokenAdditionalService } from "@/app/lib/types/codegen";
import { baggageIcons } from "@/app/lib/utils/baggageIcons";
import { type Traveller } from "@/app/lib/types/types";
import { useTravellersSelectionContext } from "@/app/lib/context/TravellersSelectionContext";
import { getTravelerPassengerTypeCode } from "@/app/lib/utils/transformTraveler";

function groupByPassengerIndex(
  data: GetFlightAvailabilityTokenAdditionalService[],
) {
  const grouped: Record<
    string,
    Record<string, GetFlightAvailabilityTokenAdditionalService[]>
  > = {};

  for (const item of data) {
    const passengerIndex = item.passengerIndex;
    const ageGroup = item.target;

    if (!grouped[ageGroup]) {
      grouped[ageGroup] = {};
    }

    if (!grouped[ageGroup][passengerIndex]) {
      grouped[ageGroup][passengerIndex] = [];
    }

    grouped[ageGroup][passengerIndex].push(item);
  }

  return grouped;
}

function TravellersListSummary({
  selectedTravellers,
  baggage,
}: {
  selectedTravellers: Traveller[];
  baggage: GetFlightAvailabilityTokenAdditionalService[];
}) {
  const t = useTranslations();
  const { getTravellerIndex } = useTravellersSelectionContext();
  const groupedByPassenger = groupByPassengerIndex(baggage);

  return (
    <div className="flex flex-col gap-2">
      {selectedTravellers.map((traveller) => {
        const index = getTravellerIndex(traveller.id);
        const ageGroup = getTravelerPassengerTypeCode(traveller);

        if (index === null) {
          return null;
        }

        const baggageInfo = groupedByPassenger[ageGroup]?.[index];

        return (
          <div
            key={traveller.id}
            className="flex flex-col gap-2 rounded-2xl bg-white"
          >
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex flex-col" data-sentry-mask>
                <span className="text-base font-medium text-text-primary">
                  {traveller.firstName} {traveller.lastName}
                </span>
                <span className="text-sm text-text-tertiary">
                  {t("travellers.passportId")}:{" "}
                  {traveller.passportNumber || "-"}
                </span>
              </div>
            </div>
            {baggageInfo ? (
              <>
                <Divider flexItem sx={{ my: 1 }} />
                <div className="flex flex-col gap-4">
                  {baggageInfo.map((service) => (
                    <div
                      key={service.bookingCode}
                      className="flex w-full items-center justify-between"
                    >
                      <div className="flex gap-6">
                        <div className="flex items-center gap-1">
                          {baggageIcons[service.service.categoryCode]}
                        </div>
                      </div>
                      <div className="flex flex-col text-end">
                        <span className="text-sm text-text-primary">
                          {service.service.name}
                        </span>
                        <span className="text-sm text-text-tertiary">
                          {service.route.arrivalCode} -{" "}
                          {service.route.departureCode}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default TravellersListSummary;
